import React, { useEffect, useMemo, useState } from "react";
import {
  LocationOn as LocationOnIcon,
  QueryBuilder as QueryBuilderIcon,
  VolumeUp as VolumeUpIcon,
  PropaneTank as PropaneTankIcon,
  LocalShipping as LocalShippingIcon,
  LocalOfferOutlined as LocalOfferOutlinedIcon,
} from "@mui/icons-material";

import { EOrderStatusDescription } from "enums/options/orders";
import { DealerOrderState, OrderProps } from "store/modules/Orders/types.store";
import { useTranslation } from "react-i18next";
import { format, intervalToDuration, isAfter, addHours } from "date-fns";
import { Box, CircularProgress, Typography, Button } from "@mui/material";

import { formatByText } from "utils";
import { formatStreetAddress } from "utils/formatByText";
import { getOrderDeliveryPersonDisplayName } from "utils/coverageAreaDriverRules";
import * as Styled from "./styles";
import { SlideModal } from "ctech-react-components";
import getOrderModalsProps from "utils/getOrderModalsProps";
import { handleUpdateOrderRequest } from "store/modules/Orders/thunk.store";
import { useAppDispatch } from "store/hooks";

interface CardAccordionProps {
  data: OrderProps;
  handleOrderUpdate: (
    id: string,
    consumerId: string,
    dealerId: string,
    paymentType: string,
    valeGas: boolean,
  ) => void;
  ordersSelected?: number | null;
  indexSelected?: number;
}

const CardAccordion: React.FC<CardAccordionProps> = React.memo(
  ({ data, handleOrderUpdate, indexSelected, ordersSelected }) => {
    const { t } = useTranslation();

    const [timeRight, setTimeRight] = useState("");
    const [delayValue, setDelayValue] = useState(0);
    const [isDelay, setIsDelayValue] = useState(false);
    const [isOpenConfirmAcceptOrder, setIsOpenConfirmAcceptOrder] =
      useState(false);

    const scheduledOrder = useMemo(
      () =>
        Boolean(data.scheduling?.enabled && data.scheduling?.startAt != null),
      [data.scheduling?.enabled, data.scheduling?.startAt],
    );

    const scheduledOrderDate = useMemo(() => {
      return data.scheduling?.startAt != null
        ? new Date(data.scheduling.startAt).getTime()
        : 0;
    }, [data.scheduling?.startAt]);

    const deliveryPersonName = getOrderDeliveryPersonDisplayName({
      driver: data?.driver,
      coverageArea: data?.coverageArea,
    });

    const dispatch = useAppDispatch();

    const handleCloseConfirmAcceptOrder = () => {
      setIsOpenConfirmAcceptOrder(false);
    };

    useEffect(() => {
      setIsDelayValue(
        isAfter(new Date(data?.createdAt ?? new Date()), new Date()),
      );
    }, [data?.createdAt]);

    useEffect(() => {
      const intervalId = setInterval(() => {
        const time = intervalToDuration({
          start: new Date(data?.createdAt ?? Date()),
          end: new Date(Date()),
        });
        if (time) {
          setDelayValue(delayValue + 1);
        }

        setTimeRight(
          format(
            new Date(
              time.years || 0,
              time.months || 0,
              time.days || 0,
              time.hours || 0,
              isDelay ? 0 : time.minutes || 0,
              isDelay ? delayValue : time.seconds,
            ),
            "HH:mm:ss",
          ),
        );
      }, 1000);
      return () => clearInterval(intervalId);
    }, [data?.createdAt, delayValue, isDelay]);

    const time = data.timeline.find(
      (value) => value.description === data.status,
    )?.occurredAt;

    const onConfirm = async () => {
      await dispatch(
        handleUpdateOrderRequest({
          dealerId: data.dealer.id,
          type: data?.dealerState === "New" ? "accept" : "finalize",
          orderId: data.id,
          body: undefined,
          consumerId: data.consumer.id,
          platform: "WEB",
          reasonNotProvidePin: undefined,
          pin: undefined,
        }),
      )
        .unwrap()
        .finally();
    };

    return (
      <Styled.Container
        colorSelected={ordersSelected === indexSelected}
        cadHeight={data.dealerState === DealerOrderState.NEW}
      >
        <Styled.CardContent
          onClick={() => {
            return handleOrderUpdate(
              data.id,
              data.consumer.id,
              data.dealer.id,
              data.payment.type,
              data?.voucher?.valeGas ?? false,
            );
          }}
        >
          <Styled.ContainerColumn>
            <Styled.Content>
              <Styled.ContentRow>
                <Styled.NameCard variant="body1">
                  {data?.consumer?.name} / {data?.dealer?.name}
                </Styled.NameCard>
                <div>
                  {data.dealerState === DealerOrderState.IN_PROGRESS && (
                    <Styled.ContainerStatus colorTheme="primary">
                      <Typography
                        color="rgba(0, 0, 0, 0.87)"
                        variant="caption"
                        fontSize={14}
                      >
                        Em andamento
                      </Typography>
                    </Styled.ContainerStatus>
                  )}
                  {data.dealerState === DealerOrderState.FINALIZED && (
                    <Styled.ContainerStatus
                      colorTheme={
                        EOrderStatusDescription[data.status as any] !==
                        "Finalizado"
                          ? "secondary"
                          : "primary"
                      }
                    >
                      <Typography
                        color={
                          EOrderStatusDescription[data.status as any] !==
                          "Finalizado"
                            ? "#9847F5"
                            : "rgba(0, 0, 0, 0.87)"
                        }
                        variant="caption"
                        fontSize={14}
                      >
                        {EOrderStatusDescription[data.status as any]}
                      </Typography>
                    </Styled.ContainerStatus>
                  )}

                  {timeRight &&
                  data.dealerState === DealerOrderState.NEW &&
                  (data.status === "Placed" ||
                    data.status === "DriverAssigned") ? (
                    <Styled.ContainerStatus colorTheme="secondary">
                      <VolumeUpIcon fontSize="small" />
                      <Typography color="#9847F5" variant="caption">
                        {t("pending")} {timeRight}
                      </Typography>
                    </Styled.ContainerStatus>
                  ) : (
                    (data.status === "Placed" ||
                      data.status === "DriverAssigned") &&
                    data.dealerState === DealerOrderState.NEW && (
                      <Box
                        sx={{ display: "flex", fontSize: "5px", width: "50px" }}
                      >
                        <CircularProgress size={25} />
                      </Box>
                    )
                  )}
                </div>
              </Styled.ContentRow>

              {scheduledOrder && scheduledOrderDate && (
                <Styled.ScheduledDeliveryOrder>
                  <LocalOfferOutlinedIcon fontSize="small" />
                  <Typography variant="body2">
                    {` Agendado para hoje entre ${format(
                      new Date(scheduledOrderDate),
                      "HH:mm",
                    )} e ${format(
                      addHours(new Date(scheduledOrderDate), 1),
                      "HH:mm",
                    )}`}
                  </Typography>
                </Styled.ScheduledDeliveryOrder>
              )}

              <Styled.NameAdress
                fontSize="14px"
                textAlign="start"
                variant="subtitle2"
              >
                <LocationOnIcon fontSize="medium" />{" "}
                {formatStreetAddress(data?.delivery?.address)}
              </Styled.NameAdress>

              <Styled.NameLocale variant="caption" textAlign="start">
                {data?.delivery?.address?.district}
              </Styled.NameLocale>

              <Styled.ContentRow>
                {data.dealerState !== "Finalized" &&
                  !!data?.delivery?.eta?.current && (
                    <Styled.NameAdress variant="subtitle2">
                      <QueryBuilderIcon
                        fontSize="small"
                        sx={{ marginTop: "-2px" }}
                      />
                      Entregar em
                      {` (${data?.delivery?.eta?.current} ${t("minutes")})`}
                    </Styled.NameAdress>
                  )}

                {data.dealerState === "Finalized" && !!time && (
                  <Styled.NameAdress variant="subtitle2">
                    <QueryBuilderIcon
                      fontSize="small"
                      sx={{ marginTop: "-2px" }}
                    />
                    {`Finalizado às ${format(new Date(time || ""), "HH:mm")}`}
                  </Styled.NameAdress>
                )}

                <Styled.NameAdress variant="subtitle2">
                  <PropaneTankIcon
                    color="primary"
                    fontSize="small"
                    sx={{ marginTop: "-2px" }}
                  />
                  {data.items.length === 1
                    ? formatByText.formatProductNameAndDescription(
                        (data.items[0] as { name?: string }).name,
                        data.items[0].description,
                      )
                    : "Vários produtos"}
                </Styled.NameAdress>
              </Styled.ContentRow>

              {deliveryPersonName && (
                <Styled.NameAdress variant="subtitle2">
                  <LocalShippingIcon fontSize="small" color="inherit" />
                  {deliveryPersonName}
                </Styled.NameAdress>
              )}
              {data.dealerState === DealerOrderState.NEW && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsOpenConfirmAcceptOrder(true);
                    }}
                    variant="contained"
                    sx={{ width: "60%", borderRadius: "100px" }}
                  >
                    {t("toAccept")}
                  </Button>
                </Box>
              )}
            </Styled.Content>
          </Styled.ContainerColumn>
        </Styled.CardContent>

        {data.dealerState === DealerOrderState.NEW &&
          isOpenConfirmAcceptOrder && (
            <SlideModal
              {...getOrderModalsProps("accept")}
              isOpen={isOpenConfirmAcceptOrder}
              onClose={handleCloseConfirmAcceptOrder}
              onConfirm={onConfirm}
            />
          )}
      </Styled.Container>
    );
  },
);

CardAccordion.displayName = "CardAccordion";

export default CardAccordion;
